import React, { memo, Suspense } from 'react';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import { ChartTypes, DEFAULT_ZEROCASE_LABEL } from './constants';
import { IProps as IChartProps } from './ChartComponent';
import cardStyles from '@dealroadshow/uikit-styles/card.module.scss';
import styles from './chart.module.scss';

interface IProps extends Partial<Omit<IChartProps, 'fallback'>> {
  className?: string,
  spinnerOverlayClassName?: string,
  isFetching?: boolean,
  zeroCaseLabel?: React.ReactNode,
  suspenseFallback?: React.ReactNode,
}

const LazyChartComponent = React.lazy(() => import('./ChartComponent'));

const getZeroCaseLabel = (zeroCaseLabel: React.ReactNode) => {
  if (React.isValidElement(zeroCaseLabel)) {
    return zeroCaseLabel;
  }

  if (typeof zeroCaseLabel === 'string') {
    return <div className={ cardStyles.cardContentZerocase }>{ zeroCaseLabel }</div>;
  }

  return <div className={ cardStyles.cardContentZerocase }>{ DEFAULT_ZEROCASE_LABEL }</div>;
};

const Chart = ({
  type = ChartTypes.Highcharts,
  mapCollectionType,
  getConfig = noop,
  configOptions = {},
  data = [],
  isFetching = false,
  className,
  spinnerOverlayClassName,
  zeroCaseLabel = null,
  suspenseFallback = <Spinner overlay isVisible />,
  allowChartUpdate = true,
  immutable = false,
  updateArgs = [true, true, false],
  containerProps = {},
  callback = noop,
}: IProps) => {
  const hasData = !isEmpty(data);
  const isZerocase = !hasData && !isFetching;

  return (
    <div className={ cn(styles.chartContainer, className) }>
      <Spinner
        isVisible={ isFetching }
        overlayClassName={ spinnerOverlayClassName }
        overlay
      />
      { hasData && (
        <Suspense fallback={ suspenseFallback }>
          <LazyChartComponent
            type={ type }
            mapCollectionType={ mapCollectionType }
            data={ data }
            getConfig={ getConfig }
            configOptions={ configOptions }
            allowChartUpdate={ allowChartUpdate }
            immutable={ immutable }
            updateArgs={ updateArgs }
            containerProps={ containerProps }
            callback={ callback }
            fallback={ suspenseFallback }
          />
        </Suspense>
      ) }
      { isZerocase && getZeroCaseLabel(zeroCaseLabel) }
    </div>
  );
};

export default memo(Chart);
